import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  boardQueryString,
  shiftDashboardApi,
  type ShiftBoardQuery,
  type ShiftNotePayload,
  type ShiftViewPayload,
} from '@/api/shiftDashboardApi'
import { useTenant } from '@/providers/TenantProvider'

/**
 * The Shift Dashboard's filter options and shared views (z8nrz7fbcm). Every key
 * carries `selectedTenantId`: views and the lists behind the filters are one
 * tenant's, so a switch must land on a cold key.
 */
const key = (tenantId: string | null, part: string) => ['shift-dashboard', tenantId, part] as const

export function useShiftFilterOptions() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: key(tenantId, 'filters'),
    queryFn: shiftDashboardApi.filterOptions,
    enabled: !!tenantId,
  })
}

export function useShiftViews() {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: key(tenantId, 'views'),
    queryFn: shiftDashboardApi.listViews,
    enabled: !!tenantId,
    // Shared: another supervisor may have just edited the view on screen.
    staleTime: 30_000,
  })
}

/** Create, update and delete a view; each refetches the list so every name and tile is the server's. */
export function useShiftViewMutations() {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  const onSuccess = () => qc.invalidateQueries({ queryKey: key(tenantId, 'views') })
  return {
    create: useMutation({ mutationFn: (body: ShiftViewPayload) => shiftDashboardApi.createView(body), onSuccess }),
    update: useMutation({
      mutationFn: ({ id, body }: { id: string; body: Partial<ShiftViewPayload> }) => shiftDashboardApi.updateView(id, body),
      onSuccess,
    }),
    remove: useMutation({ mutationFn: (id: string) => shiftDashboardApi.removeView(id), onSuccess }),
  }
}

// A shift is live: what is blocked or overdue changes by the minute, so the
// board refreshes itself while the page is open rather than relying on the
// app-wide 5-minute stale time.
const BOARD_REFRESH_MS = 60_000

/** The tiles' counts and the Attention list, for one shift and its filters. */
export function useShiftBoard(q: ShiftBoardQuery) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [...key(tenantId, 'board'), boardQueryString(q)],
    queryFn: () => shiftDashboardApi.board(q),
    enabled: !!tenantId,
    staleTime: 30_000,
    refetchInterval: BOARD_REFRESH_MS,
  })
}

/** One tile's drill-down rows. Fetched only while the tile is open. */
export function useShiftTile(tileId: string | null, q: ShiftBoardQuery) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [...key(tenantId, 'tile'), tileId, boardQueryString(q)],
    queryFn: () => shiftDashboardApi.tile(tileId!, q),
    enabled: !!tenantId && !!tileId,
    staleTime: 30_000,
    refetchInterval: BOARD_REFRESH_MS,
  })
}

type NotesQuery = Pick<ShiftBoardQuery, 'date' | 'shiftDefinitionId' | 'siteId' | 'teamId'>

export function useShiftNotes(q: NotesQuery) {
  const { selectedTenantId: tenantId } = useTenant()
  return useQuery({
    queryKey: [...key(tenantId, 'notes'), boardQueryString(q)],
    queryFn: () => shiftDashboardApi.notes(q),
    enabled: !!tenantId,
    staleTime: 30_000,
    refetchInterval: BOARD_REFRESH_MS,
  })
}

export function useShiftNoteMutations() {
  const { selectedTenantId: tenantId } = useTenant()
  const qc = useQueryClient()
  const onSuccess = () => qc.invalidateQueries({ queryKey: key(tenantId, 'notes') })
  return {
    create: useMutation({ mutationFn: (body: ShiftNotePayload) => shiftDashboardApi.createNote(body), onSuccess }),
    setResolved: useMutation({
      mutationFn: ({ id, resolved }: { id: string; resolved: boolean }) =>
        shiftDashboardApi.setNoteResolved(id, resolved),
      onSuccess,
    }),
  }
}
